import { X } from 'lucide-react';
import { type ComponentType, useMemo, useState } from 'react';
import type { GroupBase, MultiValue, StylesConfig } from 'react-select';
import Select from 'react-select';

import { useIntl, useTagAutocomplete } from '../../hooks';
import type { Tag } from '../../types/tag.js';
import { Badge } from '../ui';

const TAG_FILTER_PRELOAD_LIMIT = 100;

interface TagOption {
  value: string;
  label: string;
}

type TagSelectProps = ComponentType<{
  isMulti: true;
  inputId?: string;
  instanceId?: string;
  options: TagOption[];
  value: TagOption[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onChange: (selected: MultiValue<TagOption>) => void;
  controlShouldRenderValue: boolean;
  isLoading: boolean;
  placeholder: string;
  'aria-label'?: string;
  className?: string;
  styles: StylesConfig<TagOption, true, GroupBase<TagOption>>;
}>;

const TagSelectComponent = Select as unknown as TagSelectProps;

const tagSelectStyles: StylesConfig<TagOption, true, GroupBase<TagOption>> = {
  control: (base) => ({
    ...base,
    minHeight: '40px',
    minWidth: '14rem',
    borderColor: 'var(--color-border)',
    boxShadow: 'none',
    backgroundColor: 'var(--color-input)',
    color: 'var(--color-foreground)',
    ':hover': {
      borderColor: 'var(--color-border)',
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--color-foreground)',
  }),
  menu: (base) => ({
    ...base,
    fontSize: '0.875rem',
    backgroundColor: 'var(--color-background-light)',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? 'var(--color-background-dark)'
      : 'var(--color-background-light)',
    color: 'var(--color-foreground)',
    ':active': {
      backgroundColor: 'var(--color-background-dark)',
    },
  }),
};

interface DeviceTagFilterProps {
  onSelectedTagIdsChange: (ids: string[]) => void;
}

export default function DeviceTagFilter({ onSelectedTagIdsChange }: DeviceTagFilterProps) {
  const intl = useIntl();

  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [tagInput, setTagInput] = useState('');

  const selectedTagIds = useMemo(() => new Set(selectedTags.map((tag) => tag.id)), [selectedTags]);
  const tagSuggestionsQuery = useTagAutocomplete('', TAG_FILTER_PRELOAD_LIMIT, true);
  const tagOptions = useMemo<TagOption[]>(
    () =>
      (tagSuggestionsQuery.data ?? [])
        .filter((tag) => !selectedTagIds.has(tag.id))
        .map((tag) => ({ value: tag.id, label: tag.name })),
    [tagSuggestionsQuery.data, selectedTagIds],
  );

  const updateSelectedTags = (tags: Tag[]) => {
    setSelectedTags(tags);
    onSelectedTagIdsChange(tags.map((tag) => tag.id));
  };

  const handleTagsChange = (selected: MultiValue<TagOption>) => {
    updateSelectedTags(selected.map((option) => ({ id: option.value, name: option.label })));
  };

  const handleRemoveTag = (id: string) => {
    updateSelectedTags(selectedTags.filter((tag) => tag.id !== id));
  };

  return (
    <div className="flex flex-col gap-2">
      <TagSelectComponent
        isMulti
        inputId="devices-tag-filter"
        instanceId="devices-tag-filter"
        options={tagOptions}
        value={selectedTags.map((tag) => ({ value: tag.id, label: tag.name }))}
        inputValue={tagInput}
        onInputChange={setTagInput}
        onChange={handleTagsChange}
        controlShouldRenderValue={false}
        isLoading={tagSuggestionsQuery.isFetching}
        placeholder={intl.formatMessage({ id: 'devicesList.filterByTags' })}
        aria-label={intl.formatMessage({ id: 'devicesList.filterByTags' })}
        className="min-w-56 text-sm"
        styles={tagSelectStyles}
      />

      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="devices-selected-tags">
          {selectedTags.map((tag) => (
            <Badge
              key={tag.id}
              variant="secondary"
              className="flex items-center gap-1 pr-1 text-xs"
            >
              <span>{tag.name}</span>
              <button
                type="button"
                className="inline-flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-slate-200"
                aria-label={intl.formatMessage({ id: 'deviceTags.removeTag' }, { name: tag.name })}
                onClick={() => handleRemoveTag(tag.id)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
